/**
 * Currency formatting used across the app.
 *
 * The backend stores each order's currency alongside its total (see
 * `Order.currency` in server.py). Every UI surface that renders a price
 * should call `formatMoney(amount, order.currency)` so a customer in Turkey
 * sees ₺, an Israeli customer sees ₪, etc. — not a hard-coded $ prefix.
 *
 * Falls back to USD when the caller doesn't know the currency yet
 * (e.g. before the first fare estimate loads).
 */
export function formatMoney(amount, currency, opts = {}) {
  const cur = String(currency || "usd").toUpperCase();
  const n = Number(amount || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: cur,
      maximumFractionDigits: opts.maxFractionDigits ?? 2,
      minimumFractionDigits: opts.minFractionDigits ?? 0,
    }).format(n);
  } catch (_) {
    // Unknown ISO-4217 code — fall back to a plain code prefix.
    return `${cur} ${n.toFixed(2)}`;
  }
}

/**
 * Compact form used inside tight badges (e.g. "$1.2k").
 */
export function formatMoneyCompact(amount, currency) {
  const cur = String(currency || "usd").toUpperCase();
  const n = Number(amount || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: cur,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(n);
  } catch (_) {
    return `${cur} ${n.toFixed(0)}`;
  }
}
